import {ApiServerAlert, nanoToMilliSeconds} from "./utils";
import {usePreserveQueryParamsNavigate} from "../ui/lib/utils";
import React from "react";
import {
    AggregateMonitorEvaluationPayload,
    EvalType
} from "../../pages/alerts/MetricAlert";
import {AlertType, ThresholdComparator} from "../../pages/alerts/model";
import { getThresholdComparatorSymbol} from "../../pages/alerts/utils";
import {Button} from "../ui/button";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "../ui/dropdown-menu";
import {Copy, Download, MailIcon, MoreVertical, Trash2} from "lucide-react";
import {Dialog, DialogContent, DialogDescription, DialogTitle} from "../ui/dialog";
import axios from "../../utility/customAxios";
import {
    AlertDestinationType, EmailAlertDestination,
    PagerDutyAlertDestination,
    SlackAlertDestination,
    WebhookAlertDestination
} from "./AlertDestination";
import {FaSlack} from "react-icons/fa";
import {AiOutlineKubernetes} from "react-icons/ai";
import {MetricSpecifier, MetricType} from "../../clients/metoro/metrics";
import {alertReplacer, dashboardJsonReplacer} from "../Dashboarding/Dashboard";
import {Tooltip, TooltipContent, TooltipTrigger} from "../ui/tooltip";

const ensureStringArrayRecord = (obj: unknown): Record<string, string[]> => {
    if (obj instanceof Map) {
        return Object.fromEntries(obj) as Record<string, string[]>;
    }
    return {};
};

const formatFilters = (filters: Map<string, string[]>, isExclude?: boolean): string => {
    if (!filters || filters.size === 0) {
        return "";
    }
    return Array.from(filters.entries() as Iterable<[string, string[]]>)
        .map(([key, values]) => `${key} ${isExclude ? "!=" : "="} ${values.join(" || ")}`)
        .join(", ");
};

interface AlertDetailsProps {
    alert: ApiServerAlert | undefined;
    setAlert: (alert: ApiServerAlert | undefined) => void;
    doesNotHavePermission: boolean;
}

interface AlertBadgeProps {
    type: string;
    text: string;
    icon?: React.ReactNode;
    color?: string;
}

const AlertBadge: React.FC<AlertBadgeProps> = ({type, text, icon, color = "bg-blue-500"}) => (
    <div
        className={`inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-white shadow hover:bg-primary/80 rounded ${color} gap-2`}>
        {icon}
        {text}
    </div>
);

const AlertDestinationBadge: React.FC<{ 
    type: AlertDestinationType | "none",
    destination?: {
        type: AlertDestinationType,
        slackDestination?: SlackAlertDestination,
        pagerDutyDestination?: PagerDutyAlertDestination,
        emailDestination?: EmailAlertDestination,
        webhookDestination?: WebhookAlertDestination
    }
}> = ({type, destination}) => {
    // Generate tooltip content based on destination type
    const getTooltipContent = () => {
        if (!destination) return null;

        switch (destination.type) {
            case AlertDestinationType.Slack:
                if (destination.slackDestination) {
                    return `Channel: ${destination.slackDestination.channel}`;
                }
                return null;
            case AlertDestinationType.PagerDuty:
                if (destination.pagerDutyDestination) {
                    return `Service: ${destination.pagerDutyDestination.serviceName == "" ? "none" : destination.pagerDutyDestination.serviceName} `;
                }
                return null;
            case AlertDestinationType.Email:
                if (destination.emailDestination) {
                    return `Emails: ${destination.emailDestination.emails.join(", ")}`;
                }
                return null;
            case AlertDestinationType.Webhook:
                if (destination.webhookDestination) {
                    return `Webhook: ${destination.webhookDestination.name}`;
                }
                return null;
            default:
                return null;
        }
    };

    const tooltipContent = getTooltipContent();

    const renderBadge = () => {
        switch (type) {
            case AlertDestinationType.Slack:
                return <AlertBadge type="slack" text="Slack" icon={<FaSlack className="text-white h-4 w-4"/>}
                                color="bg-purple-500"/>;
            case AlertDestinationType.PagerDuty:
                return <AlertBadge type="pagerduty" text="PagerDuty" color="bg-[#06AC38]"/>;
            case AlertDestinationType.Email:
                return <AlertBadge type="email" text="Email" icon={<MailIcon className="text-white h-4 w-4"/>}
                                color="bg-red-400"/>;
            case AlertDestinationType.Webhook:
                return <AlertBadge type="webhook" text="Webhook" color="bg-orange-500"/>;
            case "none":
                return <AlertBadge type="none" text="None" color="bg-gray-500"/>;
            default:
                return null;
        }
    };

    if (tooltipContent) {
        return (
            <Tooltip>
                <TooltipTrigger>
                    {renderBadge()}
                </TooltipTrigger>
                <TooltipContent>
                    {tooltipContent}
                </TooltipContent>
            </Tooltip>
        );
    }

    return renderBadge();
};

const AlertStatusBadge: React.FC<{ status: string }> = ({status}) => (
    <AlertBadge
        type={status.toLowerCase()}
        text={status === "FIRING" ? "Firing" : "Ok"}
        color={status === "FIRING" ? "bg-red-500" : "bg-secondaryTransparent"}
    />
);

// There are legacy types here that is why there are so many types with inconsistent casing. TODO: Clean this up.
const AlertLegacyTypeBadge: React.FC<{ type: string }> = ({type}) => {
    switch (type.toLowerCase()) {
        case "kubernetes": // TODO: This type is inconsistent with the rest of the types in terms of casing. Fix this.
            return <AlertBadge type="kubernetes" text="Kubernetes"
                               icon={<AiOutlineKubernetes className="text-white h-4 w-4"/>} color="bg-blue-500"/>;
        case "log":
            return <AlertBadge type="log" text="Log" color="bg-blue-500"/>;
        case "logs":
            return <AlertBadge type="log" text="Log" color="bg-blue-500"/>;
        case "metric":
            return <AlertBadge type="metric" text="Metric" color="bg-blue-500"/>;
        case "trace":
            return <AlertBadge type="trace" text="Trace" color="bg-blue-500"/>;
        case "kubernetes_resource":
            return <AlertBadge type="kubernetes-resource" text="Kubernetes Resource"
                               icon={<AiOutlineKubernetes className="text-white h-4 w-4"/>} color="bg-blue-500"/>;
        case "kubernetesresource":
            return <AlertBadge type="kubernetes-resource" text="Kubernetes Resource"
                               icon={<AiOutlineKubernetes className="text-white h-4 w-4"/>} color="bg-blue-500"/>;
        default:
            return null;
    }
};

const AlertTypeBadge: React.FC<{ alert: ApiServerAlert }> = ({alert}) => {
    if (alert.type === "MultiMetric" && alert.multiMetricAlert) {
        if (alert.multiMetricAlert.filters.metricSpecifiers.length > 1) {
            return <AlertBadge type="formula" text="Formula" color="bg-blue-500"/>;
        } else if (alert.multiMetricAlert.filters.metricSpecifiers.length === 1) {
            const metricSpecType = alert.multiMetricAlert.filters.metricSpecifiers[0].metricType;
            return <AlertLegacyTypeBadge type={metricSpecType}/>
        } else if (alert.multiMetricAlert.filters.metoroQlQuery) {
            return <AlertBadge type="metoro-ql" text="MetoroQL" color="bg-blue-500"/>;
        } else {
            return <AlertBadge type="multi-metric" text="Invalid" color="bg-blue-500"/>;
        }
    } else {
        return <AlertLegacyTypeBadge type={alert.type}/>;
    }
}


const DetailRow: React.FC<{ label: string; labelWidth?: string; children: React.ReactNode }> = ({
                                                                                                    label,
                                                                                                    labelWidth = "w-[120px]",
                                                                                                    children
                                                                                                }) => (
    <div className="flex gap-4">
        <div className={`text-textmedium font-bold flex-none ${labelWidth}`}>{label}</div>
        <div className="text-textmedium flex-1 min-w-0">{children}</div>
    </div>
);

const DeleteDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onDelete: () => void;
}> = ({open, onClose, onDelete}) => (
    <Dialog open={open}>
        <DialogContent
            onInteractOutside={onClose}
            className="w-[40vw] text-textmedium bg-backgrounddark"
        >
            <DialogTitle>Are you absolutely sure?</DialogTitle>
            <DialogDescription>
                This action cannot be undone. This will permanently delete this alert
                and all firing history associated with it.
            </DialogDescription>
            <div className="flex py-4 gap-4">
                <Button
                    onClick={onDelete}
                    className="bg-red-500/20 border border-red-500 h-full flex grow shrink"
                >
                    Delete
                </Button>
            </div>
        </DialogContent>
    </Dialog>
);

const MuteDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onMuteToggle: () => void;
    isMuted: boolean;
}> = ({open, onClose, onMuteToggle, isMuted}) => (
    <Dialog open={open}>
        <DialogContent
            onInteractOutside={onClose}
            className="w-[40vw] text-textmedium bg-backgrounddark"
        >
            <DialogTitle>Would you like to {isMuted ? "unmute" : "mute"} this alert?</DialogTitle>
            <DialogDescription>
                {isMuted
                    ? "Unmuting this alert will resume notifications to your specified destinations for any new occurrences. Please note that ongoing alerts will not generate additional notifications after unmuting."
                    : "Muting this alert will stop notifications from being sent to your specified destinations."
                }
            </DialogDescription>
            <div className="flex py-4 gap-4">
                <Button
                    requiredPermission={{
                        operation: "update",
                        resource: "alerts/default/*"
                    }}
                    onClick={onMuteToggle}
                    className="bg-amber-500/20 border border-amber-500 h-full flex grow shrink rounded"
                >
                    {isMuted ? "Unmute" : "Mute"}
                </Button>
            </div>
        </DialogContent>
    </Dialog>
);

const AlertActions: React.FC<{
    alert: ApiServerAlert;
    onMuteClick: () => void;
    onDeleteClick: () => void;
    onExportClick: () => void;
    navigate: (path: string) => void;
}> = function ({alert, onMuteClick, onDeleteClick, onExportClick, navigate}) {
    const isManagedExternally = alert.alertManagement && alert.alertManagement.managingResourceType !== undefined;
    return <div className="flex items-center gap-2">
        {!isManagedExternally &&
        <Button
            requiredPermission={{
                operation: "update",
                resource: "alerts/default/*"
            }}
            onClick={() => navigate(`/new-alert?alertId=${alert.uuid}`)}
            className="bg-primarytransparent border border-primary h-9 rounded"
        >
            Edit
        </Button>}
        {
            isManagedExternally &&
            <Tooltip>
                <TooltipTrigger>
                    <Button
                        onClick={() => navigate(`/new-alert?alertId=${alert.uuid}`)}
                        className="bg-primarytransparent border border-primary h-9 rounded"
                    >
                        View
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    This alert is managed externally and cannot be edited.
                </TooltipContent>
            </Tooltip>
        }
        <Button
            requiredPermission={{
                operation: "update",
                resource: "alerts/default/*"
            }}
            onClick={onMuteClick}
            className="bg-amber-500/20 border border-amber-500 h-9 rounded"
        >
            {alert.muted ? "Unmute" : "Mute"}
        </Button>
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    className="bg-primarytransparent border border-primary h-9 rounded flex items-center gap-2 px-1">
                    <MoreVertical className="h-4 w-4"/>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-backgrounddark border-border">
                <DropdownMenuItem
                    requiredPermission={{
                        operation: "create",
                        resource: "alerts/default/*"
                    }}
                    className="text-textmedium hover:bg-primarytransparent cursor-pointer"
                    onClick={() => {
                        const {status, ...alertWithoutStatus} = alert;
                        const clonedAlert = {
                            ...alertWithoutStatus,
                            uuid: "",
                            name: `${alert.name} (Copy)`
                        };
                        navigate(`/new-alert?alertJson=${encodeURIComponent(JSON.stringify(clonedAlert, dashboardJsonReplacer))}`);
                    }}
                >
                    <Copy className="mr-2 h-4 w-4"/>
                    Clone
                </DropdownMenuItem>
                <DropdownMenuItem
                    className="text-textmedium hover:bg-primarytransparent cursor-pointer"
                    onClick={onExportClick}
                >
                    <Download className="mr-2 h-4 w-4"/>
                    Export
                </DropdownMenuItem>
                <DropdownMenuItem
                    requiredPermission={{
                        resource: "dashboards/default/*",
                        operation: "delete"
                    }}
                    className="text-red-500 hover:bg-red-500/20 cursor-pointer"
                    onClick={onDeleteClick}
                >
                    <Trash2 className="mr-2 h-4 w-4"/>
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    </div>;
};

// Alert Type-Specific Components
const KubernetesAlertDetails: React.FC<{ alert: ApiServerAlert }> = ({alert}) => {
    if (!alert.kubernetesAlert) return null;

    return (
        <div className="flex flex-col gap-4">
            <DetailRow label="Monitor">
                {alert.kubernetesAlert.monitorEvent === "pod_restart" ? "Pod Restart" :
                    alert.kubernetesAlert.monitorEvent === "pod_crash" ? "Pod Crash" : "Unknown"}
            </DetailRow>
            <DetailRow label="Clusters">
                {alert.kubernetesAlert.filters.clusters.length === 0 ? "All" :
                    alert.kubernetesAlert.filters.clusters.join(", ")}
            </DetailRow>
            <DetailRow label="Services">
                {alert.kubernetesAlert.filters.services.length === 0 ? "All" :
                    alert.kubernetesAlert.filters.services.join(", ")}
            </DetailRow>
        </div>
    );
};

const LogAlertDetails: React.FC<{ alert: ApiServerAlert }> = ({alert}) => {
    if (!alert.logAlert) return null;

    return (
        <div className="flex flex-col gap-4">
            <DetailRow label="Monitor">
                {alert.logAlert.monitorEvent === "regex_match" ? "Regex Match" : "Unknown"}
            </DetailRow>
            <DetailRow label="Clusters">
                {alert.logAlert.filters.clusters.length === 0 ? "All" :
                    alert.logAlert.filters.clusters.join(", ")}
            </DetailRow>
            <DetailRow label="Services">
                {alert.logAlert.filters.services.length === 0 ? "All" :
                    alert.logAlert.filters.services.join(", ")}
            </DetailRow>
            <DetailRow label="Filters">
                {alert.logAlert.logAlertRegexPayload?.regex
                    && <span>regex_match = {alert.logAlert.logAlertRegexPayload.regex}</span>
                }
            </DetailRow>
        </div>
    );
};

const MetricAlertDetails: React.FC<{ alert: ApiServerAlert }> = ({alert}) => {
    if (!alert.metricAlert) return null;

    return (
        <div className="flex flex-col gap-4">
            <DetailRow label="Condition" labelWidth="w-[120px]">
                <div className="text-textmedium line-clamp-3 break-all max-h-[90px] text-sm">
                    {getConditionString(alert)}
                </div>
            </DetailRow>
            {alert.metricAlert.monitorEvaluation.monitorEvaluationType === EvalType.Anomaly && 
             alert.metricAlert.monitorEvaluation.anomalyCondition && (
                <DetailRow label="Algorithm" labelWidth="w-[120px]">
                    Z-Score Anomaly Detection
                </DetailRow>
            )}
            {alert.metricAlert.monitorEvaluation.monitorEvaluationType === EvalType.Static && (
                <DetailRow label="Missing Data" labelWidth="w-[120px]">
                    treat as {alert.metricAlert.monitorEvaluation.monitorEvalutionPayload.missingDatapointBehavior}
                </DetailRow>
            )}
            {alert.metricAlert.filters.filters && Object.entries(alert.metricAlert.filters.filters).length > 0 && (
                <DetailRow label="Filter" labelWidth="w-[120px]">
                    {formatFilters(new Map(Object.entries(alert.metricAlert.filters.filters)))}
                </DetailRow>
            )}
            {alert.metricAlert.filters.excludeFilters && Object.entries(alert.metricAlert.filters.excludeFilters).length > 0 && (
                <DetailRow label="Exclude Filter" labelWidth="w-[120px]">
                    {formatFilters(new Map(Object.entries(alert.metricAlert.filters.excludeFilters)), true)}
                </DetailRow>
            )}
            {alert.metricAlert.filters.splits && alert.metricAlert.filters.splits.length > 0 && (
                <DetailRow label="Group by" labelWidth="w-[120px]">
                    {alert.metricAlert.filters.splits.join(", ")}
                </DetailRow>
            )}
        </div>
    );
};

const TraceAlertDetails: React.FC<{ alert: ApiServerAlert }> = ({alert}) => {
    if (!alert.traceAlert) return null;

    return (
        <div className="flex flex-col gap-4">
            {alert.traceAlert.monitorEvaluation.monitorEvaluationType === EvalType.Static && (
                <DetailRow label="Missing Data" labelWidth="w-[120px]">
                    treat as {alert.traceAlert.monitorEvaluation.monitorEvalutionPayload.missingDatapointBehavior}
                </DetailRow>
            )}
            <DetailRow label="Condition" labelWidth="w-[120px]">
                <div className="text-textmedium line-clamp-3 break-all max-h-[90px] text-sm">
                    {getConditionString(alert)}
                </div>
            </DetailRow>
            {alert.traceAlert.filters.filters && Object.entries(alert.traceAlert.filters.filters).length > 0 && (
                <DetailRow label="Filter" labelWidth="w-[120px]">
                    {formatFilters(new Map(Object.entries(alert.traceAlert.filters.filters)))}
                </DetailRow>
            )}
            {alert.traceAlert.filters.excludeFilters && Object.entries(alert.traceAlert.filters.excludeFilters).length > 0 && (
                <DetailRow label="Exclude Filter" labelWidth="w-[120px]">
                    {formatFilters(new Map(Object.entries(alert.traceAlert.filters.excludeFilters || {})), true)}
                </DetailRow>
            )}
            {alert.traceAlert.filters.splits && alert.traceAlert.filters.splits.length > 0 && (
                <DetailRow label="Group by" labelWidth="w-[120px]">
                    {alert.traceAlert.filters.splits.join(", ")}
                </DetailRow>
            )}
        </div>
    );
};

const KubernetesResourceAlertDetails: React.FC<{ alert: ApiServerAlert }> = ({alert}) => {
    if (!alert.kubernetesResourceAlert) return null;

    return (
        <div className="flex flex-col gap-4">
            <DetailRow label="Condition" labelWidth="w-[120px]">
                <div className="text-textmedium line-clamp-3 break-all max-h-[90px] text-sm">
                    {getConditionString(alert)}
                </div>
            </DetailRow>
            {alert.kubernetesResourceAlert.monitorEvaluation.monitorEvaluationType === EvalType.Static && (
                <DetailRow label="Missing Data" labelWidth="w-[120px]">
                    treat
                    as {alert.kubernetesResourceAlert.monitorEvaluation.monitorEvalutionPayload.missingDatapointBehavior}
                </DetailRow>
            )}
            {alert.kubernetesResourceAlert.filters.filters && Object.entries(alert.kubernetesResourceAlert.filters.filters).length > 0 && (
                <DetailRow label="Filter" labelWidth="w-[120px]">
                    {formatFilters(new Map(Object.entries(alert.kubernetesResourceAlert.filters.filters)))}
                </DetailRow>
            )}
            {alert.kubernetesResourceAlert.filters.excludeFilters && Object.entries(alert.kubernetesResourceAlert.filters.excludeFilters).length > 0 && (
                <DetailRow label="Exclude Filter" labelWidth="w-[120px]">
                    {formatFilters(new Map(Object.entries(alert.kubernetesResourceAlert.filters.excludeFilters)), true)}
                </DetailRow>
            )}
            {alert.kubernetesResourceAlert.filters.splits && alert.kubernetesResourceAlert.filters.splits.length > 0 && (
                <DetailRow label="Group by" labelWidth="w-[120px]">
                    {alert.kubernetesResourceAlert.filters.splits.join(", ")}
                </DetailRow>
            )}
        </div>
    );
};

function renderFormulaForMultiMetric(metrics: MetricSpecifier[]) {
    const alphabet: string[] = [];
    for (let i = 0; i < metrics.length; i++) {
        alphabet.push(String.fromCharCode(65 + i));
    }

    return metrics.map((metric: MetricSpecifier, index: number) => {
        return <div className={"flex gap-1"}>
            <div className={"text-textlight font-semibold"}>{alphabet[index].toLowerCase()}</div>
            : {getMetricSpecifierDetails(metric, index)}
        </div>
    })
}

function getMetricSpecifierDetails(metric: MetricSpecifier, index: number) {
    const type = metric.metricType;
    var seed = "";
    switch (type) {
        case MetricType.Metric:
            seed = `${metric.aggregation}(${metric.metricName})`;
            break
        case MetricType.Logs:
            seed = `count of logs`;
            break
        case MetricType.Trace:
            seed = `${metric.aggregation} of traces`;
            break
        case MetricType.Kubernetes:
            seed = `${metric.aggregation} of kubernetes resource`;
            break
        default:
            seed = `Unknown`;
    }
    if (((metric.filters && metric.filters?.size > 0) || (metric.excludeFilters && metric.excludeFilters?.size > 0) || (metric.regexes && metric.regexes?.length > 0) || (metric.excludeRegexes && metric.excludeRegexes?.length > 0))) {
        seed += ` where ${getFilter(metric, index)}`;
    }
    return seed;
}

function getFilter(metric: MetricSpecifier, index: number) {
    const filters = metric.filters || {};
    const excludeFilters = metric.excludeFilters || {};
    const regexes = metric.regexes || [];
    const excludeRegexes = metric.excludeRegexes || [];
    const allFilters: string[] = [];

    // Add regular filters
    Object.entries(ensureStringArrayRecord(filters)).forEach(([key, values]) => {
        // @ts-ignore
        allFilters.push(`${key} = ${values.join(" || ")}`);
    });

    // Add exclude filters
    Object.entries(ensureStringArrayRecord(excludeFilters)).forEach(([key, values]) => {
        allFilters.push(`${key} != ${values.join(" || ")}`);
    });

    // Add regexes if metric is logs
    if (metric.metricType === MetricType.Logs) {
        if (regexes.length > 0) {
            allFilters.push(`regex = ${regexes.join(" || ")}`);
        }

        // Add exclude regexes
        if (excludeRegexes.length > 0) {
            allFilters.push(`regex != ${excludeRegexes.join(" || ")}`);
        }
    }

    return allFilters.length > 0 ? allFilters.join(" && ") : ""
}

function renderFilter(metric: MetricSpecifier, index: number) {
    const allFilters = getFilter(metric, index);
    return (
        <div key={`metric-${index}`} className="text-sm">
            <div>
                {allFilters}
            </div>
        </div>
    );
}

const MultiMetricAlertDetails: React.FC<{ alert: ApiServerAlert }> = ({alert}) => {
    if (!alert.multiMetricAlert) return null;

    const {multiMetricAlert} = alert;
    const metrics = multiMetricAlert.filters.metricSpecifiers;
    const [metoroQLQuery, setMetoroQLQuery] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    // Fetch MetoroQL query when component mounts or alert changes
    React.useEffect(() => {
        // If we already have a MetoroQL query, use it
        if (multiMetricAlert.filters.metoroQlQuery) {
            setMetoroQLQuery(multiMetricAlert.filters.metoroQlQuery.query);
            return;
        }

        // If we have metric specifiers, convert them to MetoroQL
        if (metrics.length > 0) {
            setIsLoading(true);
            // Import the function dynamically to avoid circular dependencies
            import("../../clients/metoro/metrics").then(({ convertMetricSpecifierToMetoroQL }) => {
                // Create a deep copy of metrics to avoid mutating the original data
                const metricsCopy = metrics.map(metric => ({
                    ...metric,
                    filters: metric.filters instanceof Map ? new Map(metric.filters) : new Map(Object.entries(metric.filters || {}).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])),
                    excludeFilters: metric.excludeFilters instanceof Map ? new Map(metric.excludeFilters) : new Map(Object.entries(metric.excludeFilters || {}).map(([k, v]) => [k, Array.isArray(v) ? v : [v]]))
                }));

                convertMetricSpecifierToMetoroQL(
                    metricsCopy,
                    multiMetricAlert.filters.formula ? [multiMetricAlert.filters.formula] : undefined
                )
                .then(response => {
                    if (response.queries && response.queries.length > 0) {
                        setMetoroQLQuery(response.queries[0]);
                    }
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error("Error converting to MetoroQL:", error);
                    setIsLoading(false);
                });
            });
        }
    }, [alert, multiMetricAlert, metrics]);

    // Determine what to display for the condition
    const getDisplayCondition = () => {
        if (isLoading) {
            return "Loading...";
        }
        //
        // if (metoroQLQuery) {
        //     return `${metoroQLQuery} ${getThresholdComparatorSymbol(multiMetricAlert.alarmCondition.condition)} ${multiMetricAlert.alarmCondition.threshold}`;
        // }

        return getConditionString(alert);
    };

    return (
        <div className="flex flex-col gap-4">
            <DetailRow label="Condition" labelWidth="w-[120px]">
                <div className="text-textmedium line-clamp-3 break-all max-h-[90px] text-sm">
                    {getDisplayCondition()}
                </div>
            </DetailRow>
            {multiMetricAlert.monitorEvaluation.monitorEvaluationType === EvalType.Anomaly && 
             multiMetricAlert.monitorEvaluation.anomalyCondition && (
                <DetailRow label="Algorithm" labelWidth="w-[120px]">
                    Z-Score Anomaly Detection
                </DetailRow>
            )}
            {multiMetricAlert.monitorEvaluation.monitorEvaluationType === EvalType.Static && (
                <DetailRow label="Missing Data" labelWidth="w-[120px]">
                    treat as {multiMetricAlert.monitorEvaluation.monitorEvalutionPayload.missingDatapointBehavior}
                </DetailRow>
            )}
        </div>
    );
};

// Helper function to get condition string
const getConditionString = (alert: ApiServerAlert): string => {
    const typedAlert = alert.metricAlert || alert.traceAlert || alert.kubernetesResourceAlert || alert.multiMetricAlert;
    if (!typedAlert) return "";

    // Handle anomaly detection
    if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Anomaly) {
        const anomalyCondition = typedAlert.monitorEvaluation.anomalyCondition;
        if (anomalyCondition && anomalyCondition.algorithm === "zScore" && anomalyCondition.zScoreParameters) {
            const zParams = anomalyCondition.zScoreParameters;
            let metricString = "";

            // Get the metric expression
            if (alert.type === AlertType.MultiMetric && alert.multiMetricAlert) {
                if (alert.multiMetricAlert.filters.formula) {
                    metricString = alert.multiMetricAlert.filters.formula.formula;
                } else if (alert.multiMetricAlert.filters.metoroQlQuery) {
                    metricString = alert.multiMetricAlert.filters.metoroQlQuery.query || "metric";
                } else {
                    metricString = alert.multiMetricAlert.filters.metricSpecifiers
                        .filter(m => !m.shouldNotReturn)
                        .map(m => {
                            if (m.metricType === "metric") {
                                return `${m.aggregation}(${m.metricName})`;
                            }
                            if (m.metricType === "kubernetes_resource") {
                                let condition = `${m.aggregation} of kubernetes_resource`;
                                if (m.jsonPath) {
                                    condition += ` with jsonPath: ${m.jsonPath}`;
                                }
                                return condition;
                            }
                            if (m.metricType === "logs") {
                                return `count of logs`;
                            }
                            return `${m.aggregation} of ${m.metricType}`;
                        })
                        .join(", ");
                }
            } else if (alert.type === AlertType.Metric && alert.metricAlert) {
                metricString = alert.metricAlert.filters.aggregation + "(" + alert.metricAlert.filters.metricName + ")";
            } else if (alert.type === AlertType.Trace && alert.traceAlert) {
                metricString = alert.traceAlert.filters.aggregation + " of traces";
            } else if (alert.type === AlertType.KubernetesResource && alert.kubernetesResourceAlert) {
                metricString = alert.kubernetesResourceAlert.filters.aggregation + " of kubernetes_resource";
            }
            let operatorSymbol = zParams.operator === "greaterThan" ? ">" : zParams.operator === "lessThan" ? "<" : "<>";

            let s = `zScore(${zParams.dataPointsInBaselineWindow}, ${metricString}) ${operatorSymbol} ${zParams.threshold}`;
            return s;
        }
    }

    let evaluationPrefix = "";
    if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Static) {
        const datapointsToAlarm = typedAlert.monitorEvaluation.monitorEvalutionPayload.datapointsToAlarm || 1;
        const evaluationWindow = typedAlert.monitorEvaluation.monitorEvalutionPayload.evaluationWindow || 1;
        evaluationPrefix = `${datapointsToAlarm} out of ${evaluationWindow} datapoints: `;
    }


    let metricString = "";
    if (alert.type === AlertType.MultiMetric && alert.multiMetricAlert) {
        if (alert.multiMetricAlert.filters.formula) {
            metricString = `${alert.multiMetricAlert.filters.formula.formula} ${getThresholdComparatorSymbol(alert.multiMetricAlert.alarmCondition.condition)} ${alert.multiMetricAlert.alarmCondition.threshold}`;
        } else {
            metricString = alert.multiMetricAlert.filters.metricSpecifiers
                .filter(m => !m.shouldNotReturn)
                .map(m => {
                    if (m.metricType === "metric") {
                        return `${m.aggregation}(${m.metricName})`;
                    }
                    if (m.metricType === "kubernetes_resource") {
                        let condition = `${m.aggregation} of kubernetes_resource`;
                        if (m.jsonPath) {
                            condition += ` with jsonPath: ${m.jsonPath}`;
                        }
                        return condition;
                    }
                    if (m.metricType === "logs") {
                        return `count of logs`;
                    }
                    return `${m.aggregation} of ${m.metricType}`;
                })
                .join(", ") + ` ${getThresholdComparatorSymbol(alert.multiMetricAlert.alarmCondition.condition)} ${alert.multiMetricAlert.alarmCondition.threshold}`;
        }
    }

    // Get metric string e.g. average:cpu_usage_namespace
    if (alert.type === AlertType.Metric && alert.metricAlert) {
        metricString = alert.metricAlert.filters.metricName;
        metricString = alert.metricAlert.filters.aggregation + ":" + metricString;
    }
    if (alert.type === AlertType.Trace && alert.traceAlert) {
        metricString = alert.traceAlert.filters.aggregation;
    }
    if (alert.type === AlertType.KubernetesResource && alert.kubernetesResourceAlert) {
        metricString = alert.kubernetesResourceAlert.filters.aggregation;
    }

    // Wrap metric string with functions e.g. monotonic_difference(average:cpu_usage_namespace)
    let functionsString = metricString;
    const alertWithFunctions = alert.metricAlert || alert.traceAlert || alert.kubernetesResourceAlert;
    if (alertWithFunctions?.filters.functions) {
        alertWithFunctions.filters.functions.forEach((func) => {
            functionsString = func.functionType + "(" + functionsString + ")";
        });
    }

    // Get threshold comparator string e.g. > 0.5
    let thresholdComparatorString = "";
    if (alert.type === AlertType.Metric && alert.metricAlert) {
        thresholdComparatorString = `${getThresholdComparatorSymbol(alert.metricAlert.alarmCondition.condition as ThresholdComparator)} ${alert.metricAlert.alarmCondition.threshold}`;
    } else if (alert.type === AlertType.Trace && alert.traceAlert) {
        thresholdComparatorString = `${getThresholdComparatorSymbol(alert.traceAlert.alarmCondition.condition as ThresholdComparator)} ${alert.traceAlert.alarmCondition.threshold}`;
    } else if (alert.type === AlertType.KubernetesResource && alert.kubernetesResourceAlert) {
        thresholdComparatorString = `${getThresholdComparatorSymbol(alert.kubernetesResourceAlert.alarmCondition.condition as ThresholdComparator)} ${alert.kubernetesResourceAlert.alarmCondition.threshold}`;
    }

    let evaluationString = "";
    if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Static) {
        evaluationString = evaluationPrefix + functionsString;
    } else if (typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Aggregate) {
        const typedEvaluationPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
        let aggregateFunction = typedEvaluationPayload.evaluationFunction.toString();
        if (typedEvaluationPayload.evaluationSplits) {
            typedEvaluationPayload.evaluationSplits.forEach((split) => {
                aggregateFunction += " of " + split;
            });
        }

        let typeName = "";
        if (alert.type === AlertType.Metric) {
            typeName = "metric";
        }
        if (alert.type === AlertType.Trace) {
            typeName = "trace";
        }
        if (alert.type === AlertType.KubernetesResource) {
            typeName = "kubernetes resource";
        }

        aggregateFunction = `${aggregateFunction}(every ${typedEvaluationPayload.window}${typedEvaluationPayload.windowUnit.toString()})`;
        evaluationString = `${aggregateFunction} of ${typeName} ${functionsString}`;
    }

    return evaluationString + " " + thresholdComparatorString;
};

const ExportDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onExportJSON: () => void;
    onExportYAML: () => void;
    alert: ApiServerAlert;
}> = ({open, onClose, onExportJSON, onExportYAML, alert}) => {
    const isMultiMetric = alert.type === "MultiMetric" || alert.type === (AlertType.MultiMetric as string);
    const hasAggregateCondition = alert.multiMetricAlert && alert.multiMetricAlert.monitorEvaluation.monitorEvaluationType === EvalType.Aggregate;

    return (
        <Dialog open={open}>
            <DialogContent
                onInteractOutside={onClose}
                className="w-[95vw] max-w-md md:max-w-2xl text-textmedium bg-backgrounddark"
            >
                <DialogTitle>Export Alert</DialogTitle>
                <DialogDescription>
                    Choose the format to export the alert:
                </DialogDescription>
                <div className="flex flex-col py-4 gap-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Button
                            onClick={onExportJSON}
                            className="bg-primarytransparent border border-primary h-full flex grow shrink rounded"
                        >
                            Legacy Format
                        </Button>
                        {isMultiMetric && !hasAggregateCondition ? (
                            <Button
                                onClick={onExportYAML}
                                className="bg-primarytransparent border border-primary h-full flex grow shrink rounded"
                            >
                                K8s Config Map Alerts Format
                            </Button>
                        ) : (
                            <Tooltip>
                                <TooltipTrigger>
                                    <Button
                                        disabled={true}
                                        className="bg-primarytransparent border border-primary h-full flex grow shrink rounded opacity-50"
                                    >
                                        K8s Config Map Alerts Format
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {!isMultiMetric 
                                        ? "Deprecated alert types are not supported for config map alerts. Please first convert your alerts to MultiMetric format."
                                        : "Aggregate conditions are not supported for config map alerts. Please use static threshold conditions instead."}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                    <div className="mt-2 text-sm space-y-3 text-textlight">
                        <div>
                            <span className="font-semibold">Legacy Format:</span> The legacy format with the JSON structure. This format is deprecated and will be phased out in future versions. Currently, it can be used for importing alerts using the UI.
                        </div>
                        <div>
                            <span className="font-semibold">K8s Config Map Alerts Format:</span> The recommended format that uses a more compact and extensible structure. This format is used for creating and managing alerts using Kubernetes ConfigMaps. Export your alerts in this format and use them in your Kubernetes cluster.
                            <div className="mt-2">
                                <a
                                    href="https://metoro.io/docs/alerts-monitoring/create_alert#managing-alerts-using-kubernetes-configmaps"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline flex items-center gap-1 flex-wrap"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                    </svg>
                                    <span>Learn more about ConfigMap alerts in our documentation</span>
                                </a>
                            </div>
                        </div>
                        <div className="text-amber-400">
                            ⚠️ Note: Please migrate to the Config Map format for future compatibility.
                        </div>
                        <div className="text-textdark italic">
                            Please contact the Metoro team for more information.
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export function AlertDetails({alert, setAlert, doesNotHavePermission}: AlertDetailsProps) {
    const navigate = usePreserveQueryParamsNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [muteDialogOpen, setMuteDialogOpen] = React.useState(false);
    const [exportDialogOpen, setExportDialogOpen] = React.useState(false);

    if (!alert) {
        if (doesNotHavePermission) {
            return (
                <div className="flex justify-center items-center w-full h-full">
                    <div className="text-textmedium">You do not have permission to view this alert</div>
                </div>
            );
        }
        return (
            <div className="flex justify-center items-center w-full h-full">
                <div className="text-textmedium">No alert selected</div>
            </div>
        );
    }

    const handleDelete = async () => {
        await axios.delete(`/api/v1/alert?alertId=${alert.uuid}`);
        navigate("/alerts");
    };

    const handleMuteToggle = async () => {
        const endpoint = alert.muted ? '/api/v1/alert/unmute' : '/api/v1/alert/mute';
        await axios.put(endpoint, {alertUUID: alert.uuid});
        const response = await axios.get(`/api/v1/alert?alertId=${alert.uuid}`);
        setAlert(response.data);
        setMuteDialogOpen(false);
    };

    const handleExportJSON = () => {
        const {status, ...alertWithoutStatus} = alert;
        // TODO: Units HACK HACK
        const blob = new Blob([JSON.stringify(alertWithoutStatus, alertReplacer, 2)], {type: 'application/json'});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${alert.name.toLowerCase().replace(/\s+/g, '-')}-alert.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setExportDialogOpen(false);
    };

    const handleExportYAML = async () => {
        try {
            const response = await axios.get(`/api/v1/alert/export/yaml?alertId=${alert.uuid}`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], {type: 'application/yaml'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${alert.name.toLowerCase().replace(/\s+/g, '-')}-alert.yaml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting alert as YAML:', error);
        }
        setExportDialogOpen(false);
    };

    return (
        <div className="text-textmedium border flex h-max bg-backgroundmedium rounded">
            <div className="flex grow flex-col gap-4 m-4">
                <div className="flex justify-between">
                    <div className="text-lg font-semibold">Summary</div>
                    <AlertActions
                        alert={alert}
                        onMuteClick={() => setMuteDialogOpen(true)}
                        onDeleteClick={() => setDeleteDialogOpen(true)}
                        onExportClick={() => setExportDialogOpen(true)}
                        navigate={navigate}
                    />
                </div>

                <div className="grid grid-rows-1 grid-cols-2">
                    <div className="flex flex-col gap-4">
                        <DetailRow label="Name">{alert.name}</DetailRow>
                        <DetailRow label="Description">{alert.description}</DetailRow>
                        {
                            alert.alertManagement && alert.alertManagement.managingResourceType &&
                            <DetailRow label="Managed by">
                                {
                                    alert.alertManagement.managingResourceType === "KubernetesConfigMap" ?
                                        <AlertBadge type="kubernetes" text="Kubernetes Config Map"
                                                   icon={<AiOutlineKubernetes className="text-white h-4 w-4"/>}
                                                   color="bg-blue-500"/> :
                                        <AlertBadge type="external" text="External" color="bg-gray-500"/>
                                }
                            </DetailRow>
                        }
                        {
                            alert.alertManagement && alert.alertManagement.managingResourceIdentifier &&
                            <DetailRow label="Managing Resource">
                                    <AlertBadge type="external" text={alert.alertManagement.managingResourceIdentifier}/>
                            </DetailRow>
                        }
                        <DetailRow label="Destinations">
                            <div className="flex flex-wrap gap-2">
                                {alert.destinations.length > 0
                                    ? alert.destinations.map((destination, index) => (
                                        <AlertDestinationBadge key={index} type={destination.type} destination={destination}/>
                                    ))
                                    : <AlertDestinationBadge type="none"/>
                                }
                            </div>
                        </DetailRow>
                        <DetailRow label="Status">
                            <AlertStatusBadge status={alert.status || "OK"}/>
                        </DetailRow>
                    </div>

                    <div className="flex flex-col gap-4">
                        <DetailRow label="Type">
                            <AlertTypeBadge alert={alert}/>
                        </DetailRow>

                        {alert.type === "Kubernetes" && <KubernetesAlertDetails alert={alert}/>}
                        {alert.type === "Log" && <LogAlertDetails alert={alert}/>}
                        {alert.type === "Metric" && <MetricAlertDetails alert={alert}/>}
                        {alert.type === "Trace" && <TraceAlertDetails alert={alert}/>}
                        {alert.type === "KubernetesResource" && <KubernetesResourceAlertDetails alert={alert}/>}
                        {alert.type === "MultiMetric" && <MultiMetricAlertDetails alert={alert}/>}
                    </div>
                </div>
            </div>

            <DeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onDelete={handleDelete}
            />

            <MuteDialog
                open={muteDialogOpen}
                onClose={() => setMuteDialogOpen(false)}
                onMuteToggle={handleMuteToggle}
                isMuted={alert.muted}
            />

            <ExportDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                onExportJSON={handleExportJSON}
                onExportYAML={handleExportYAML}
                alert={alert}
            />
        </div>
    );
}
